

import React, { useEffect, useState } from 'react';
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp,
  getDoc,
  doc,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';

export default function FinanceDashboard() {
  const [admin, setAdmin] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [credits, setCredits] = useState([]);
  const [stripeRevenue, setStripeRevenue] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists() && snap.data().isMaster === true) {
          setAdmin(user.uid);
        } else {
          alert('🚫 Access denied');
          navigate('/dashboard');
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!admin) return;

    const bookingQ = query(collection(db, 'bookingLogs'), orderBy('timestamp', 'desc'));
    const subscriptionQ = query(collection(db, 'subscriptionLogs'), orderBy('timestamp', 'desc'));
    const creditQ = query(collection(db, 'creditLogs'), orderBy('timestamp', 'desc'));
    const expenseQ = query(collection(db, 'manualExpenses'), orderBy('timestamp', 'desc'));
    const stripeRevQ = query(collection(db, 'stripeRevenueLogs'), orderBy('created', 'desc'));
    const payoutsQ = query(collection(db, 'stripePayoutLogs'), orderBy('arrivalDate', 'desc'));

    const unsub1 = onSnapshot(bookingQ, (snap) => setBookings(snap.docs.map((doc) => doc.data())));
    const unsub2 = onSnapshot(subscriptionQ, (snap) => setSubscriptions(snap.docs.map((doc) => doc.data())));
    const unsub3 = onSnapshot(creditQ, (snap) =>
      setCredits(snap.docs.filter(d => d.data().type === 'purchase').map((doc) => doc.data()))
    );
    const unsub4 = onSnapshot(expenseQ, (snap) =>
      setExpenses(snap.docs.map((doc) => ({ ...doc.data(), id: doc.id })))
    );
    const unsub5 = onSnapshot(stripeRevQ, (snap) => setStripeRevenue(snap.docs.map((doc) => doc.data())));
    const unsub6 = onSnapshot(payoutsQ, (snap) => setPayouts(snap.docs.map((doc) => doc.data())));

    setLoading(false);
    return () => {
      unsub1(); unsub2(); unsub3(); unsub4(); unsub5(); unsub6();
    };
  }, [admin]);

  const filteredExpenses = expenses.filter((e) => {
    const matchReason = e.reason.toLowerCase().includes(filter.toLowerCase());
    const matchAdmin = e.adminId.toLowerCase().includes(filter.toLowerCase());
    const date = e.timestamp?.toDate?.();
    const matchDate =
      (!dateRange.from || new Date(dateRange.from) <= date) &&
      (!dateRange.to || date <= new Date(dateRange.to));
    return (matchReason || matchAdmin) && matchDate;
  });

  const totalRevenue =
    bookings.reduce((sum, b) => sum + (b.amount || 0), 0) +
    subscriptions.reduce((sum, s) => sum + (s.amount || 0), 0) +
    credits.reduce((sum, c) => sum + (c.amount || 0), 0) +
    stripeRevenue.reduce((sum, r) => sum + (r.amount || 0), 0);

  const totalExpenses =
    filteredExpenses.reduce((sum, e) => sum + Math.abs(e.amount || 0), 0) +
    payouts.reduce((sum, p) => sum + Math.abs(p.amount || 0), 0);

  const profit = totalRevenue - totalExpenses;

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!amount || !reason) return;

    try {
      if (editingId) {
        await updateDoc(doc(db, 'manualExpenses', editingId), {
          amount: Number(amount),
          reason,
        });
      } else {
        await addDoc(collection(db, 'manualExpenses'), {
          adminId: admin,
          amount: Number(amount),
          reason,
          timestamp: Timestamp.now(),
        });
      }
      setAmount('');
      setReason('');
      setEditingId(null);
    } catch (err) {
      console.error('❌ Error saving expense:', err);
    }
  };

  const handleEditExpense = (expense) => {
    setAmount(expense.amount);
    setReason(expense.reason);
    setEditingId(expense.id);
  };

  const handleDeleteExpense = async (id) => {
    if (!id) return;
    if (window.confirm('Delete this expense?')) {
      try {
        await deleteDoc(doc(db, 'manualExpenses', id));
      } catch (err) {
        console.error('❌ Failed to delete:', err);
      }
    }
  };

  const exportCSV = () => {
    const headers = ['#', 'Date', 'Timestamp', 'Amount ($)', 'Reason', 'Admin UID'];
    const rows = filteredExpenses.map((e, i) => {
      const fullDate = e.timestamp?.toDate?.();
      const date = fullDate?.toLocaleDateString() || '';
      const time = fullDate?.toLocaleTimeString() || '';

      return [i + 1, date, time, Math.abs(e.amount || 0).toFixed(2), `"${e.reason}"`, e.adminId];
    });

    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });

    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = `expenses_${new Date().toISOString().split('T')[0]}.csv`;
    downloadLink.click();
  };

  if (loading) return <div style={{ padding: 40 }}>🔄 Loading finance data...</div>;

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>← Back</button>
      <h2 style={styles.header}>📊 Finance Dashboard</h2>

      <div style={styles.section}>
        <h3 style={styles.subheader}>💵 Totals</h3>
        <p><strong>Total Revenue:</strong> ${totalRevenue.toFixed(2)}</p>
        <p><strong>Total Expenses:</strong> ${totalExpenses.toFixed(2)}</p>
        <p><strong>Profit:</strong> ${profit.toFixed(2)}</p>
      </div>

      <div style={styles.section}>
        <h3 style={styles.subheader}>➕ {editingId ? 'Edit' : 'Add'} Manual Expense</h3>
        <form onSubmit={handleAddExpense} style={styles.form}>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} style={styles.input} placeholder="Amount" />
          <input value={reason} onChange={(e) => setReason(e.target.value)} style={styles.input} placeholder="Reason (e.g. domain)" />
          <button type="submit" style={styles.submitButton}>{editingId ? 'Save Changes' : 'Add Expense'}</button>
        </form>
      </div>

      <div style={styles.section}>
        <h3 style={styles.subheader}>🔍 Filter</h3>
        <input style={styles.input} placeholder="Search by reason or admin" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
          <input type="date" value={dateRange.from} onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })} style={styles.input} />
          <input type="date" value={dateRange.to} onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })} style={styles.input} />
        </div>
        <button onClick={exportCSV} style={{ ...styles.submitButton, marginTop: '1rem' }}>📤 Export CSV</button>
      </div>

      <div style={styles.section}>
        <h3 style={styles.subheader}>🧾 Manual Expenses</h3>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Timestamp</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Reason</th>
              <th style={styles.th}>Admin</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredExpenses.map((e) => (
              <tr key={e.id}>
                <td style={styles.td}>{e.timestamp?.toDate?.().toLocaleString() || '—'}</td>
                <td style={styles.td}>${e.amount.toFixed(2)}</td>
                <td style={styles.td}>{e.reason}</td>
                <td style={styles.td}>{e.adminId}</td>
                <td style={styles.td}>
                  <button onClick={() => handleEditExpense(e)} style={{ marginRight: 8 }}>✏️</button>
                  <button onClick={() => handleDeleteExpense(e.id)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem', backgroundColor: '#f8f9fa', fontFamily: 'system-ui, sans-serif', minHeight: '100vh',
  },
  backButton: {
    marginBottom: '1rem', padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #ccc', backgroundColor: '#eee', cursor: 'pointer',
  },
  header: {
    fontSize: '1.5rem', marginBottom: '1rem', color: '#0f5132',
  },
  subheader: {
    marginBottom: '0.5rem', fontWeight: 'bold',
  },
  section: {
    marginBottom: '2rem',
  },
  form: {
    display: 'flex', flexDirection: 'column', maxWidth: '400px',
  },
  input: {
    marginBottom: '0.75rem', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc',
  },
  submitButton: {
    padding: '0.5rem', borderRadius: '6px', backgroundColor: '#0f5132', color: '#fff', border: 'none', cursor: 'pointer',
  },
  table: {
    width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', boxShadow: '0 0 10px rgba(0,0,0,0.05)',
  },
  th: {
    borderBottom: '2px solid #ccc', padding: '0.75rem', backgroundColor: '#e6f2ff', textAlign: 'left',
  },
  td: {
    borderBottom: '1px solid #ddd', padding: '0.75rem',
  },
};
